// Serviço de geração de etiquetas para impressoras de gôndola
// Suporta: Argox (PPLA) e Elgin (PPLA/ZPL)

const LABEL_WIDTH = 50; // mm
const LABEL_HEIGHT = 30; // mm
const DPI = 203; // dots per inch (padrão Argox/Elgin)

// Converter mm para dots
const mmToDots = (mm) => Math.round((mm * DPI) / 25.4);

// Formatar preço para exibição
const formatPrice = (price) => {
  return `R$ ${price.toFixed(2).replace('.', ',')}`;
};

// Gerar código EAN-13 checksum
const calculateEAN13Checksum = (code) => {
  if (code.length !== 12) return null;
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += parseInt(code[i]) * (i % 2 === 0 ? 1 : 3);
  }
  const checksum = (10 - (sum % 10)) % 10;
  return code + checksum;
};

// Gerar etiqueta em formato PPLA (Argox/Elgin)
const generatePPLA = (product, quantity = 1) => {
  const barcode = product.barcode || '';
  const name = (product.name || '').substring(0, 20).toUpperCase();
  const price = formatPrice(product.price || 0);
  
  // PPLA Commands
  let ppla = '';
  
  for (let i = 0; i < quantity; i++) {
    ppla += `
N
q${mmToDots(LABEL_WIDTH)}
Q${mmToDots(LABEL_HEIGHT)},24
D7
S2
ZT
A50,20,0,3,1,1,N,"${name}"
A50,60,0,4,1,1,N,"${price}"
${barcode ? `B50,100,0,E30,2,4,60,B,"${barcode}"` : ''}
P1
`;
  }
  
  return ppla.trim();
};

// Gerar etiqueta em formato ZPL (Zebra compatível - Elgin)
const generateZPL = (product, quantity = 1) => {
  const barcode = product.barcode || '';
  const name = (product.name || '').substring(0, 20).toUpperCase();
  const price = formatPrice(product.price || 0);
  
  let zpl = '';
  
  for (let i = 0; i < quantity; i++) {
    zpl += `
^XA
^PW${mmToDots(LABEL_WIDTH)}
^LL${mmToDots(LABEL_HEIGHT)}
^FO20,20^A0N,25,25^FD${name}^FS
^FO20,55^A0N,35,35^FD${price}^FS
${barcode ? `^FO20,100^BY2^BCN,50,Y,N,N^FD${barcode}^FS` : ''}
^XZ
`;
  }
  
  return zpl.trim();
};

// Gerar lote de etiquetas
const generateBatch = (products, printerType = 'argox') => {
  const generator = printerType === 'zpl' ? generateZPL : generatePPLA;
  
  let batch = '';
  products.forEach(({ product, quantity }) => {
    batch += generator(product, quantity) + '\n';
  });
  
  return batch;
};

// Imprimir via Web Serial API (conexão direta USB)
const printViaSerial = async (data, baudRate = 9600) => {
  if (!('serial' in navigator)) {
    throw new Error('Web Serial API não suportada neste navegador. Use Chrome ou Edge.');
  }
  
  try {
    const port = await navigator.serial.requestPort();
    await port.open({ baudRate });
    
    const encoder = new TextEncoder();
    const writer = port.writable.getWriter();
    await writer.write(encoder.encode(data));
    writer.releaseLock();
    
    await port.close();
    return true;
  } catch (error) {
    throw new Error('Erro ao imprimir: ' + error.message);
  }
};

// Download do arquivo de impressão
const downloadPrintFile = (data, filename = 'etiquetas.prn') => {
  const blob = new Blob([data], { type: 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Imprimir via compartilhamento de rede (Windows)
const printViaNetwork = async (data, printerPath) => {
  // Esta função requer um servidor intermediário ou extensão do navegador
  // Por segurança, navegadores não permitem acesso direto a impressoras de rede
  throw new Error('Impressão via rede requer configuração adicional. Use a opção de download.');
};

// Preview da etiqueta em canvas
const generatePreview = (product, canvas) => {
  const ctx = canvas.getContext('2d');
  const scale = 3; // Escala para melhor visualização
  
  canvas.width = LABEL_WIDTH * scale;
  canvas.height = LABEL_HEIGHT * scale;
  
  // Fundo branco
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  
  // Borda
  ctx.strokeStyle = '#cccccc';
  ctx.lineWidth = 1;
  ctx.strokeRect(0, 0, canvas.width, canvas.height);
  
  // Nome do produto
  ctx.fillStyle = '#000000';
  ctx.font = `bold ${10 * scale}px Arial`;
  const name = (product.name || '').substring(0, 20).toUpperCase();
  ctx.fillText(name, 5 * scale, 12 * scale);
  
  // Preço
  ctx.font = `bold ${14 * scale}px Arial`;
  ctx.fillText(formatPrice(product.price || 0), 5 * scale, 24 * scale);
  
  // Código de barras (representação simplificada)
  if (product.barcode) {
    ctx.font = `${6 * scale}px monospace`;
    ctx.fillText(product.barcode, 5 * scale, 29 * scale);
    
    // Barras simuladas
    ctx.fillStyle = '#000000';
    const barWidth = 1;
    const startX = 5 * scale;
    const barY = 20 * scale;
    const barHeight = 6 * scale;
    
    for (let i = 0; i < 40; i++) {
      if (Math.random() > 0.5) {
        ctx.fillRect(startX + i * barWidth * scale, barY, barWidth * scale, barHeight);
      }
    }
  }
};

export const etiquetaService = {
  generatePPLA,
  generateZPL,
  generateBatch,
  printViaSerial,
  downloadPrintFile,
  generatePreview,
  calculateEAN13Checksum,
  LABEL_WIDTH,
  LABEL_HEIGHT
};

export default etiquetaService;
